import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getAdminStockAllocationRun,
  listAdminStockAllocationRunItems,
  listAdminStockAllocationRuns,
  retryAdminStockAllocationRun,
} from '../../admin/adminApi'
import { Button, Card, I, Pill, gridColumns, mobileButtonStyle } from '../../components/ui'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import type { StockAllocationRunDetail, StockAllocationRunItem, StockAllocationRunSummary } from '../../stockAllocation/types'
import { tableContainerStyle, tdStyle, thStyle } from './adminPageFormatters'
import { EmptyState, ErrorState, LoadingState } from './adminPageHelpers'

interface Props {
  accessToken: string
  onOpenOrganization: (organizationId: number) => void
}

export function AdminStockAllocationDiagnosticsPage({ accessToken, onOpenOrganization }: Props) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [runs, setRuns] = useState<StockAllocationRunSummary[]>([])
  const [runId, setRunId] = useState<number | null>(null)
  const [detail, setDetail] = useState<StockAllocationRunDetail | null>(null)
  const [items, setItems] = useState<StockAllocationRunItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [detailError, setDetailError] = useState(false)
  const [detailReload, setDetailReload] = useState(0)
  const [actionError, setActionError] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const detailRequestId = useRef(0)

  const loadRuns = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const next = await listAdminStockAllocationRuns(accessToken)
      setRuns(next)
      setRunId(current => current != null && next.some(run => run.id === current) ? current : next[0]?.id ?? null)
      if (next.length === 0) {
        setDetail(null)
        setItems([])
      }
    } catch { setError(true) } finally { setLoading(false) }
  }, [accessToken])

  useEffect(() => {
    void Promise.resolve().then(loadRuns)
  }, [loadRuns])
  useEffect(() => {
    if (runId == null) return
    const requestId = ++detailRequestId.current
    void Promise.resolve().then(async () => {
      setLoading(true)
      setDetailError(false)
      setDetail(null)
      setItems([])
      try {
        const [nextDetail, nextItems] = await Promise.all([
          getAdminStockAllocationRun(accessToken, runId),
          listAdminStockAllocationRunItems(accessToken, runId),
        ])
        if (requestId !== detailRequestId.current) return
        setDetail(nextDetail)
        setItems(nextItems.content)
      } catch {
        if (requestId !== detailRequestId.current) return
        setDetail(null)
        setItems([])
        setDetailError(true)
      } finally {
        if (requestId === detailRequestId.current) setLoading(false)
      }
    })
    return () => { detailRequestId.current += 1 }
  }, [accessToken, runId, detailReload])

  if (loading && runs.length === 0) return <LoadingState label={t('admin.stockDiagnostics.loading')} />
  if (error && runs.length === 0) return <ErrorState title={t('admin.stockDiagnostics.error.title')} body={t('admin.stockDiagnostics.error.body')} onRetry={loadRuns} />
  if (runs.length === 0) return <EmptyState title={t('admin.stockDiagnostics.empty.title')} body={t('admin.stockDiagnostics.empty.body')} />
  if (detailError) return <ErrorState title={t('admin.stockDiagnostics.error.title')} body={t('admin.stockDiagnostics.error.body')} onRetry={() => setDetailReload(value => value + 1)} />

  const run = detail?.run
  const date = (value: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))

  async function retry() {
    if (!run) return
    setRetrying(true)
    setActionError(false)
    try {
      const next = await retryAdminStockAllocationRun(accessToken, run.id)
      await loadRuns()
      setRunId(next.run.id)
    } catch {
      setActionError(true)
    } finally { setRetrying(false) }
  }

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <Card>
        <label style={{ display: 'grid', gap: 6 }}><span style={{ fontSize: 12, color: 'var(--fg-muted)', fontWeight: 800 }}>{t('admin.stockDiagnostics.run')}</span><select value={runId ?? ''} onChange={event => setRunId(Number(event.target.value))} style={selectStyle}>{runs.map(candidate => <option key={candidate.id} value={candidate.id}>{candidate.organizationName} · {candidate.connectionName ?? candidate.provider} · {date(candidate.createdAt)} · #{candidate.id}</option>)}</select></label>
      </Card>

      {run && <>
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto'), gap: 16 }}>
            <div><div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}><h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 21 }}>{t('admin.stockDiagnostics.title', { provider: run.provider })}</h2><Pill tone={run.failed > 0 ? 'danger' : 'success'}>{run.status}</Pill></div><div style={{ color: 'var(--fg-muted)', marginTop: 6 }}>{run.organizationName} · {run.connectionName} · {date(run.createdAt)}</div></div>
            <Button kind="secondary" onClick={() => onOpenOrganization(run.organizationId)} style={mobileButtonStyle(isMobile)}>{t('admin.stockDiagnostics.inspectConnection')}</Button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(4, minmax(0, 1fr))'), gap: 10, marginTop: 18 }}>{[['attempted', run.attempted], ['pushed', run.pushed], ['failed', run.failed], ['skipped', run.skipped]].map(([key, value]) => <div key={key} style={{ padding: 14, background: 'var(--ink-50)', borderRadius: 'var(--r-md)' }}><div style={{ fontSize: 24, fontWeight: 900 }}>{value}</div><div style={{ color: 'var(--fg-muted)', fontSize: 12 }}>{t(`admin.stockDiagnostics.metrics.${key}`)}</div></div>)}</div>
          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(2, minmax(0, 1fr))'), gap: 12, marginTop: 16 }}><Diagnostic label={t('admin.stockDiagnostics.transport')} value={t('admin.stockDiagnostics.transportCompleted')} /><Diagnostic label={t('admin.stockDiagnostics.itemResult')} value={run.failed > 0 ? t('admin.stockDiagnostics.itemRejected', { count: run.failed }) : t('admin.stockDiagnostics.itemAccepted')} /><Diagnostic label={t('admin.stockDiagnostics.taskName')} value={detail.taskName} code /><Diagnostic label={t('admin.stockDiagnostics.taskInstance')} value={detail.taskInstanceId} code /></div>
          {run.provider === 'NOON' && run.failed > 0 && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}><Button kind="danger" icon={I.refresh} disabled={retrying} onClick={() => void retry()}>{retrying ? t('admin.stockDiagnostics.retrying') : t('admin.stockDiagnostics.retry')}</Button></div>}
          {actionError && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 800, marginTop: 10 }}>{t('admin.stockDiagnostics.retryFailed')}</div>}
        </Card>

        <Card>
          <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 19 }}>{t('admin.stockDiagnostics.codes')}</h2>
          <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>{detail.failedCodes.map(group => <div key={group.code} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderBottom: '1px solid var(--border-subtle)', paddingBottom: 8 }}><code>{group.code}</code><strong>{group.count.toLocaleString()}</strong></div>)}</div>
        </Card>

        <div style={tableContainerStyle}><table style={{ width: '100%', borderCollapse: 'collapse' }}><thead><tr><th style={thStyle}>{t('admin.stockDiagnostics.table.product')}</th><th style={thStyle}>{t('admin.stockDiagnostics.table.sku')}</th><th style={thStyle}>{t('admin.stockDiagnostics.table.quantity')}</th><th style={thStyle}>{t('admin.stockDiagnostics.table.code')}</th><th style={thStyle}>{t('admin.stockDiagnostics.table.message')}</th></tr></thead><tbody>{items.map(item => <tr key={item.id}><td style={tdStyle}>{item.productName}</td><td style={{ ...tdStyle, fontFamily: 'var(--font-mono)' }}>{item.productSku}</td><td style={tdStyle}>{item.availableQuantity ?? '—'}</td><td style={{ ...tdStyle, fontFamily: 'var(--font-mono)' }}>{item.providerCode ?? item.reason}</td><td style={{ ...tdStyle, maxWidth: 360, overflowWrap: 'anywhere' }}>{item.providerMessage ?? t('admin.common.none')}</td></tr>)}</tbody></table></div>
      </>}
    </div>
  )
}

function Diagnostic({ label, value, code }: { label: string; value: string; code?: boolean }) {
  return <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', padding: 12 }}><div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800 }}>{label}</div><div style={{ color: 'var(--fg)', marginTop: 4, fontWeight: 700, fontFamily: code ? 'var(--font-mono)' : undefined, overflowWrap: 'anywhere' }}>{value}</div></div>
}

const selectStyle = { width: '100%', padding: '11px 14px', fontSize: 14, fontFamily: 'inherit', color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)', borderRadius: 10 } as const
